import React, { useEffect, useState } from "react";
import { fetchAll, createDoc, updateDocById, deleteDocById, orderBy, WithId } from "../lib/firestoreData";

interface Etkinlik {
  baslik: string;
  tarih: string;
  yer: string;
  sira: number;
  aciklama?: string;
  kategori?: string;
  saat?: string;
}
type Row = Etkinlik & WithId;

const COLLECTION = "etkinlikler";
const KATEGORILER = ["Kültür", "Gençlik", "Aile", "Genel"];
// sira string (input), kayıtta number'a çevrilir. kategori varsayılan "Genel".
const bos = { baslik: "", tarih: "", yer: "", sira: "", aciklama: "", kategori: "Genel", saat: "" };

const EtkinliklerPage: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<{ baslik: string; tarih: string; yer: string; sira: string; aciklama: string; kategori: string; saat: string }>(bos);
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      setRows(await fetchAll<Etkinlik>(COLLECTION, orderBy("sira", "asc")));
    } catch (e) {
      console.error("Etkinlikler okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(bos); setEditId(""); setFormError(""); };
  const openEdit = (r: Row) => {
    // Eski kayıtlarda yeni alanlar olmayabilir → güvenli varsayılanlar.
    setForm({
      baslik: r.baslik, tarih: r.tarih, yer: r.yer, sira: String(r.sira ?? ""),
      aciklama: r.aciklama ?? "", kategori: r.kategori ?? "Genel", saat: r.saat ?? "",
    });
    setEditId(r.id);
    setFormError("");
  };
  const close = () => { setEditId(null); setFormError(""); };

  const save = async () => {
    setFormError("");
    if (!form.baslik.trim() || !form.tarih.trim() || form.sira.trim() === "") {
      setFormError("Başlık, tarih ve sıra zorunludur.");
      return;
    }
    const siraNum = parseInt(form.sira, 10);
    if (Number.isNaN(siraNum)) { setFormError("Sıra geçerli bir sayı olmalıdır."); return; }
    setSaving(true);
    try {
      const payload: Etkinlik = {
        baslik: form.baslik.trim(), tarih: form.tarih.trim(), yer: form.yer.trim(), sira: siraNum,
        aciklama: form.aciklama.trim(), kategori: form.kategori.trim() || "Genel", saat: form.saat.trim(),
      };
      if (editId) await updateDocById<Etkinlik>(COLLECTION, editId, payload);
      else await createDoc<Etkinlik>(COLLECTION, payload);
      close();
      await load();
    } catch (e) {
      console.error("Kayıt başarısız:", e);
      setFormError("Kayıt başarısız, lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: Row) => {
    if (!window.confirm(`"${r.baslik}" silinsin mi?`)) return;
    try { await deleteDocById(COLLECTION, r.id); await load(); }
    catch (e) { console.error("Silme başarısız:", e); window.alert("Silme başarısız."); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Etkinlikler</h2>
          <p className="text-sm text-gray-500 mt-0.5">{rows.length} etkinlik · mobil Ana Sayfa "Yaklaşan Etkinlikler"</p>
        </div>
        <button onClick={openNew} className="bg-[#8B1428] hover:bg-[#6f0f20] text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors">
          <i className="fa-solid fa-plus" /> Yeni Etkinlik
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Etkinlikler yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-calendar-day text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz etkinlik eklenmemiş</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-16">Sıra</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Başlık</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Yer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400 font-mono">{r.sira}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{r.baslik}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{r.tarih}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{r.yer || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => openEdit(r)} className="text-blue-600 hover:text-blue-800 text-xs font-semibold">Düzenle</button>
                      <button onClick={() => remove(r)} className="text-gray-400 hover:text-red-500"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editId !== null && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={close} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative max-h-[90vh] overflow-y-auto">
              <button onClick={close} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><i className="fa-solid fa-xmark text-lg" /></button>
              <h3 className="text-lg font-bold text-gray-800 mb-5">{editId ? "Etkinliği Düzenle" : "Yeni Etkinlik"}</h3>
              <div className="space-y-4">
                <FieldE label="Başlık *"><input className={inputClsE} value={form.baslik} onChange={e => setForm({ ...form, baslik: e.target.value })} /></FieldE>
                <div className="grid grid-cols-2 gap-3">
                  <FieldE label="Tarih *"><input className={inputClsE} value={form.tarih} onChange={e => setForm({ ...form, tarih: e.target.value })} placeholder="15 Temmuz 2026" /></FieldE>
                  <FieldE label="Saat"><input className={inputClsE} value={form.saat} onChange={e => setForm({ ...form, saat: e.target.value })} placeholder="12:00" /></FieldE>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldE label="Yer"><input className={inputClsE} value={form.yer} onChange={e => setForm({ ...form, yer: e.target.value })} placeholder="Bursa" /></FieldE>
                  <FieldE label="Kategori">
                    <select className={inputClsE} value={form.kategori} onChange={e => setForm({ ...form, kategori: e.target.value })}>
                      {KATEGORILER.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </FieldE>
                </div>
                <FieldE label="Sıra *"><input type="number" className={inputClsE} value={form.sira} onChange={e => setForm({ ...form, sira: e.target.value })} placeholder="1" /></FieldE>
                <FieldE label="Açıklama"><textarea rows={3} className={inputClsE} value={form.aciklama} onChange={e => setForm({ ...form, aciklama: e.target.value })} placeholder="Etkinlik açıklaması..." /></FieldE>
                {formError && <div className="text-red-600 text-sm">{formError}</div>}
                <div className="flex gap-3 pt-2">
                  <button onClick={close} className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-50">İptal</button>
                  <button onClick={save} disabled={saving} className="flex-1 bg-[#8B1428] hover:bg-[#6f0f20] text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-60">
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const inputClsE = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30 text-gray-900";
const FieldE: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    {children}
  </div>
);

export default EtkinliklerPage;
